import { useRef } from 'react';
import { BULK_ACTION_ENUM } from '@/shared/model';
import { DEFAULT_CONTROL_MODAL_VALUE, selectControlModal, useReportStore } from '@/entities/report';
import getBulkActionMenu from './handler';
import type { BulkActionByCellProps } from './index';

/**
 * Backs the bulk-action menu attached to a single beat template cell. Builds the icon menu once per template mode and, on a pick, either opens the swap-all-templates-by-id modal or arms the chosen per-cell bulk action in the report store.
 *
 * @param props - Disabled flag, template mode flag and the report-store tab key.
 * @returns The disabled flag, the menu entry ref and the menu click handler.
 */
const useBulkActionByCell = ({ isDisabled, isTemplate, keyRecoil }: BulkActionByCellProps) => {
  const bulkActionMenu = useRef(getBulkActionMenu(isTemplate));

  const setBulkActionAction = useReportStore((s) => s.actions.setBulkActionByCell);
  /**
   * Arms the per-cell bulk action for this tab. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setBulkAction = (v: Parameters<typeof setBulkActionAction>[1]) =>
    setBulkActionAction(keyRecoil, v);
  const setControlModalAction = useReportStore((s) => s.actions.setControlModal);
  /**
   * Writes this tab's modal visibility flags. Forwards the value or updater to the matching report-store action, bound to this tab's keyRecoil key.
   *
   * @param v - The next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setControlModal = (v: Parameters<typeof setControlModalAction>[1]) =>
    setControlModalAction(keyRecoil, v);
  /**
   * Reads this tab's modal visibility flags from the report store. Applies the control-modal selector to a snapshot instead of subscribing, so the menu click handler sees the current flags.
   *
   * @returns The tab's control modal flags.
   */
  const getControlModal = () => selectControlModal(keyRecoil)(useReportStore.getState());

  // IControlModal (Recoil/model.ts) doesn't declare the swap-template fields these
  // components read/write; they exist at runtime but not in the shared type.
  /**
   * Handles a pick from a cell's bulk-action menu. Opens the swap-all-templates-by-id modal for the swap entry, remounting it after a short reset delay when another swap modal is already showing, and otherwise arms the picked per-cell action.
   *
   * @param info - The menu click payload whose item props carry the bulk action value.
   * @returns Nothing.
   */
  const onClickItem = (info: any) => {
    const { item } = info || {};
    const value = item?.props?.value;
    if (value === BULK_ACTION_ENUM.SWAP_TEMPLATE) {
      const controlModal = getControlModal() as any;
      if (controlModal.isShowSwapTemplate || controlModal.isShowSwapAllTemplateByPage) {
        setControlModal(DEFAULT_CONTROL_MODAL_VALUE);
        setTimeout(() => {
          setControlModal({
            ...DEFAULT_CONTROL_MODAL_VALUE,
            isShowSwapAllTemplateById: true,
          } as any);
        }, 100);
      } else {
        setControlModal({ ...DEFAULT_CONTROL_MODAL_VALUE, isShowSwapAllTemplateById: true } as any);
      }
      return;
    }
    setBulkAction(value);
  };

  return {
    isDisabled,
    bulkActionMenu,
    onClickItem,
  };
};

export default useBulkActionByCell;
